/* eslint-disable complexity */
/* eslint-disable max-lines */
import { AppletDesignVersionConfig_V3 } from '../../../types/mes/applet-designs/v3/appletDesign.v3.zod';
import { AppletDesignVersionConfig_V4 } from '../../../types/mes/applet-designs/latest/appletDesign.zod';
import type { z } from 'zod';
import type { BaseNodeCfg, NodeCfg as NodeCfg_V4 } from '../../../types/mes/applet-designs/latest/flow/nodes';
import { varOrConstSchema as varOrConstSchema_v3 } from '../../../types/mes/applet-designs/v3/var-providers/var-provider.zod';
import { varOrConstSchema as varOrConstSchema_v4 } from '../../../types/mes/applet-designs/latest/var.zod';
import type { AppletVariableValue as AppletVariableValue_V4 } from '../../../types/mes/applet-designs/latest/var.zod';
import type { varConditionSchema as varConditionSchema_v3 } from '../../../types/mes/applet-designs/v3/var-providers/conditional.zod';
import type { varConditionSchema as varConditionSchema_v4 } from '../../../types/mes/applet-designs/latest/conditional.zod';
import type { NodeCfg as NodeCfg_V3 } from '../../../types/mes/applet-designs/v3/flow/nodes';
import { impliedAppletEventIds } from '../get-applet-events';
import { DeployParamKind } from '../deployParam.zod';
import { FisDataTimingEnum } from '@redviking/argonaut-util/types/db';

type OldVarOrConst = z.infer<ReturnType<typeof varOrConstSchema_v3>>;
type NewVarOrConst<T extends AppletVariableValue_V4 = AppletVariableValue_V4> = z.infer<ReturnType<typeof varOrConstSchema_v4<T>>>;
const oldVarOrConstToNewVarOrConst = <T extends AppletVariableValue_V4 = AppletVariableValue_V4>(oldVarOrConst: OldVarOrConst): NewVarOrConst<T> => {
    if (oldVarOrConst.type === 'var') {
        return {
            type: 'var',
            varId: oldVarOrConst.var,
        };
    }
    return {
        type: 'const',
        val: oldVarOrConst.val.val as T,
    };
};

const migrateGridLayout = <T extends keyof AppletDesignVersionConfig_V3['screens'][number]['gridLayoutAspectRatios']>(oldGridConfig: AppletDesignVersionConfig_V3['screens'][number]['gridLayoutAspectRatios'][T]): AppletDesignVersionConfig_V4['screens'][number]['gridLayoutAspectRatios'][T] => ({
    enabled: oldGridConfig.enabled,
    gridConfig: {
        id: oldGridConfig.gridConfig.id,
        name: oldGridConfig.gridConfig.name,
        rows: oldGridConfig.gridConfig.rows,
        border: oldGridConfig.gridConfig.border,
        margin: oldGridConfig.gridConfig.margin,
        padding: oldGridConfig.gridConfig.padding,
        columns: oldGridConfig.gridConfig.columns,
        elevation: oldGridConfig.gridConfig.elevation,
        borderRadius: oldGridConfig.gridConfig.borderRadius,
        backgroundColor: oldVarOrConstToNewVarOrConst(oldGridConfig.gridConfig.backgroundColor),
    },
    linkedGridCells: oldGridConfig.linkedGridCells.map<AppletDesignVersionConfig_V4['screens'][number]['gridLayoutAspectRatios'][T]['linkedGridCells'][number]>(lgc => {
        if (lgc.type === 'infoBox') {
            const ret: AppletDesignVersionConfig_V4['screens'][number]['gridLayoutAspectRatios'][T]['linkedGridCells'][number] & { type: 'infoBox' } = {
                id: lgc.id,
                type: lgc.type,
                name: lgc.name,
                border: lgc.border,
                margin: lgc.margin,
                padding: lgc.padding,
                elevation: lgc.elevation,
                coordinates: lgc.coordinates,
                borderRadius: lgc.borderRadius,
                parentGridId: lgc.parentGridId,
                backgroundColor: oldVarOrConstToNewVarOrConst<string>(lgc.backgroundColor),
                additionalCellProperties: {
                    headerUseItalics: false,
                    contentUseItalics: false,
                    headerSourceVarTestValue: '',
                    contentSourceVarTestValue: '',
                    headerFontSize: lgc.additionalCellProperties.headerFontSize,
                    contentFontSize: lgc.additionalCellProperties.contentFontSize,
                    headerFontWeight: lgc.additionalCellProperties.headerFontWeight,
                    contentFontWeight: lgc.additionalCellProperties.contentFontWeight,
                    headerVerticalAlignment: lgc.additionalCellProperties.headerVerticalAlignment,
                    contentVerticalAlignment: lgc.additionalCellProperties.contentVerticalAlignment,
                    headerHorizontalAlignment: lgc.additionalCellProperties.headerHorizontalAlignment,
                    contentHorizontalAlignment: lgc.additionalCellProperties.contentHorizontalAlignment,
                    headerSourceVar: oldVarOrConstToNewVarOrConst<string>(lgc.additionalCellProperties.headerSourceVar),
                    contentSourceVar: oldVarOrConstToNewVarOrConst<string>(lgc.additionalCellProperties.contentSourceVar),
                    headerFontColor: {
                        type: 'const',
                        val: 'black',
                    },
                    contentFontColor: {
                        type: 'const',
                        val: 'black',
                    },
                },
            };
            return ret;
        } else if (lgc.type === 'regular') {
            const ret: AppletDesignVersionConfig_V4['screens'][number]['gridLayoutAspectRatios'][T]['linkedGridCells'][number] & { type: 'regular' } = {
                id: lgc.id,
                type: lgc.type,
                name: lgc.name,
                border: lgc.border,
                margin: lgc.margin,
                padding: lgc.padding,
                elevation: lgc.elevation,
                coordinates: lgc.coordinates,
                borderRadius: lgc.borderRadius,
                parentGridId: lgc.parentGridId,
                additionalCellProperties: lgc.additionalCellProperties,
                backgroundColor: oldVarOrConstToNewVarOrConst<string>(lgc.backgroundColor),
            };
            return ret;
        } else if (lgc.type === 'subGrid') {
            const ret: AppletDesignVersionConfig_V4['screens'][number]['gridLayoutAspectRatios'][T]['linkedGridCells'][number] & { type: 'subGrid' } = {
                id: lgc.id,
                type: lgc.type,
                name: lgc.name,
                rows: lgc.rows,
                border: lgc.border,
                margin: lgc.margin,
                padding: lgc.padding,
                columns: lgc.columns,
                elevation: lgc.elevation,
                coordinates: lgc.coordinates,
                borderRadius: lgc.borderRadius,
                parentGridId: lgc.parentGridId,
                additionalCellProperties: lgc.additionalCellProperties,
                backgroundColor: oldVarOrConstToNewVarOrConst<string>(lgc.backgroundColor),
            };
            return ret;
        } else if (lgc.type === 'tabGroup') {
            const ret: AppletDesignVersionConfig_V4['screens'][number]['gridLayoutAspectRatios'][T]['linkedGridCells'][number] & { type: 'tabGroup' } = {
                id: lgc.id,
                type: lgc.type,
                name: lgc.name,
                border: lgc.border,
                margin: lgc.margin,
                padding: lgc.padding,
                elevation: lgc.elevation,
                coordinates: lgc.coordinates,
                borderRadius: lgc.borderRadius,
                parentGridId: lgc.parentGridId,
                backgroundColor: oldVarOrConstToNewVarOrConst<string>(lgc.backgroundColor),
                additionalCellProperties: {
                    ...lgc.additionalCellProperties,
                    strictTabs: false,
                },
            };
            return ret;
        } else if (lgc.type === 'tabInstance') {
            const ret: AppletDesignVersionConfig_V4['screens'][number]['gridLayoutAspectRatios'][T]['linkedGridCells'][number] & { type: 'tabInstance' } = {
                id: lgc.id,
                idx: lgc.idx,
                rows: lgc.rows,
                type: lgc.type,
                name: lgc.name,
                border: lgc.border,
                margin: lgc.margin,
                columns: lgc.columns,
                padding: lgc.padding,
                elevation: lgc.elevation,
                coordinates: lgc.coordinates,
                borderRadius: lgc.borderRadius,
                parentGridId: lgc.parentGridId,
                additionalCellProperties: lgc.additionalCellProperties,
                backgroundColor: oldVarOrConstToNewVarOrConst<string>(lgc.backgroundColor),
            };
            return ret;
        } else {
            const u: never = lgc;
            throw new Error(`Unknown grid cell: ${u}`);
        }
    }),
});

type Condition_V3 = z.infer<typeof varConditionSchema_v3>;
type Condition_V4 = z.infer<typeof varConditionSchema_v4>;
const migrateVarChangeCondition = (oldCondition: Condition_V3): Condition_V4 & { evalTrigger: 'varChange' } => {
    return {
        evalTrigger: 'varChange',
        boolOperation: oldCondition.boolOperation,
        comparisons: oldCondition.comparisons.map(comp => {
            if (comp.type === 'eq') {
                const ret: (Condition_V4 & { evalTrigger: 'varChange' })['comparisons'][number] & { type: 'eq'} = {
                    not: comp.not,
                    type: 'eq',
                    operands: [
                        {
                            type: 'var',
                            varId: comp.operands[0].varName,
                        },
                        comp.operands[1].type === 'var'
                        ? {
                            type: 'var',
                            varId: comp.operands[1].varName,
                        }
                        : comp.operands[1],
                    ],
                };
                return ret;
            } else if (comp.type === 'gt') {
                const ret: (Condition_V4 & { evalTrigger: 'varChange' })['comparisons'][number] & { type: 'gt'} = {
                    not: comp.not,
                    type: 'gt',
                    operands: [
                        {
                            type: 'var',
                            varId: comp.operands[0].varName,
                        },
                        comp.operands[1].type === 'var'
                        ? {
                            type: 'var',
                            varId: comp.operands[1].varName,
                        }
                        : comp.operands[1],
                    ],
                };
                return ret;
            } else if (comp.type === 'isset') {
                const ret: (Condition_V4 & { evalTrigger: 'varChange' })['comparisons'][number] & { type: 'isset'} = {
                    not: comp.not,
                    type: 'isset',
                    operands: [
                        {
                            type: 'var',
                            varId: comp.operands[0].varName,
                        },
                    ],
                };
                return ret;
            } else if (comp.type === 'lt') {
                const ret: (Condition_V4 & { evalTrigger: 'varChange' })['comparisons'][number] & { type: 'lt'} = {
                    not: comp.not,
                    type: 'lt',
                    operands: [
                        {
                            type: 'var',
                            varId: comp.operands[0].varName,
                        },
                        comp.operands[1].type === 'var'
                        ? {
                            type: 'var',
                            varId: comp.operands[1].varName,
                        }
                        : comp.operands[1],
                    ],
                };
                return ret;
            } else if (comp.type === 'regexp') {
                const ret: (Condition_V4 & { evalTrigger: 'varChange' })['comparisons'][number] & { type: 'regexp'} = {
                        not: comp.not,
                    type: 'regexp',
                    operands: [
                        {
                            type: 'var',
                            varId: comp.operands[0].varName,
                        },
                        comp.operands[1],
                    ],
                };
                return ret;
            } else if (comp.type === 'type') {
                const ret: (Condition_V4 & { evalTrigger: 'varChange' })['comparisons'][number] & { type: 'type'} = {
                    not: comp.not,
                    type: 'type',
                    operands: [
                        {
                            type: 'var',
                            varId: comp.operands[0].varName,
                        },
                        comp.operands[1],
                    ],
                };
                return ret;
            } else {
                const u: never = comp;
                throw new Error(`Unknown comparison type: ${u}`);
            }
        }),
    };
};

const migrationCondition = (oldCondition: Condition_V3): Condition_V4 => {
    if (oldCondition.evalTrigger === 'event') {
        return {
            evalTrigger: 'event',
            eventTrigger: oldCondition.eventTrigger || 'Unknown Event Id',
        };
    } else if (oldCondition.evalTrigger === 'varChange') {
        return migrateVarChangeCondition(oldCondition);
    } else {
        const u: never = oldCondition.evalTrigger;
        throw new Error(`Conditional could not be migrated. Received: ${u}`);
    }
};

export const upgradeFromV3ToV4 = (oldCfg: AppletDesignVersionConfig_V3): AppletDesignVersionConfig_V4 => {
    const userAppletEvents: AppletDesignVersionConfig_V4['userAppletEvents'] = {
        ...oldCfg.userAppletEvents,
    };
    
    // PROCESS VARIABLES
    const appletVariables: AppletDesignVersionConfig_V4['appletVariables'] = {};
    const oldVariables = oldCfg.varProviders.find(vp => vp.type === 'local');
    if (oldVariables) {
        let idx = 0;
        for (const oldAppletVar of oldVariables.attrs.vars) {
            const maybeSparkplugDeviceMetrics = oldCfg.sparkplugDeviceMetrics.find(spdm => spdm.name === oldAppletVar.name);
            if (oldAppletVar.type === 'localVar') {
                if (oldAppletVar.varCfg.type === 'bool') {
                    appletVariables[oldAppletVar.name] = {
                        idx,
                        type: 'bool',
                        kind: 'local',
                        name: oldAppletVar.name,
                        defaultValue: oldAppletVar.varCfg.val,
                        sparkplug: {
                            tracked: Boolean(maybeSparkplugDeviceMetrics),
                            writable: maybeSparkplugDeviceMetrics?.permissions.write ?? false,
                        },
                    };
                } else if (oldAppletVar.varCfg.type === 'str') {
                    appletVariables[oldAppletVar.name] = {
                        idx,
                        type: 'str',
                        kind: 'local',
                        name: oldAppletVar.name,
                        defaultValue: oldAppletVar.varCfg.val,
                        sparkplug: {
                            tracked: Boolean(maybeSparkplugDeviceMetrics),
                            writable: maybeSparkplugDeviceMetrics?.permissions.write ?? false,
                        },
                    };
                } else if (oldAppletVar.varCfg.type === 'num') {
                    appletVariables[oldAppletVar.name] = {
                        idx,
                        type: 'num',
                        kind: 'local',
                        name: oldAppletVar.name,
                        defaultValue: oldAppletVar.varCfg.val,
                        sparkplug: {
                            tracked: Boolean(maybeSparkplugDeviceMetrics),
                            writable: maybeSparkplugDeviceMetrics?.permissions.write ?? false,
                        },
                    };
                } else if (oldAppletVar.varCfg.type === 'null') {
                    appletVariables[oldAppletVar.name] = {
                        idx,
                        type: 'empty',
                        kind: 'local',
                        name: oldAppletVar.name,
                        defaultValue: oldAppletVar.varCfg.val,
                        sparkplug: {
                            tracked: Boolean(maybeSparkplugDeviceMetrics),
                            writable: maybeSparkplugDeviceMetrics?.permissions.write ?? false,
                        },
                    };
                } else {
                    // the only other types are Error and pending, but applet variables could not have been initialized to these in the config
                }
            } else if (oldAppletVar.type === 'deployParam') {
                if (oldAppletVar.varCfg.type === 'bool') {
                    appletVariables[oldAppletVar.name] = {
                        idx,
                        type: 'bool',
                        kind: 'deployParam',
                        name: oldAppletVar.name,
                        defaultValue: oldAppletVar.varCfg.val,
                        deployParamKind: DeployParamKind.localVarValue,
                        sparkplug: {
                            tracked: Boolean(maybeSparkplugDeviceMetrics),
                            writable: maybeSparkplugDeviceMetrics?.permissions.write ?? false,
                        },
                    };
                } else if (oldAppletVar.varCfg.type === 'str') {
                    appletVariables[oldAppletVar.name] = {
                        idx,
                        type: 'str',
                        kind: 'deployParam',
                        name: oldAppletVar.name,
                        defaultValue: oldAppletVar.varCfg.val,
                        deployParamKind: DeployParamKind.localVarValue,
                        sparkplug: {
                            tracked: Boolean(maybeSparkplugDeviceMetrics),
                            writable: maybeSparkplugDeviceMetrics?.permissions.write ?? false,
                        },
                    };
                } else if (oldAppletVar.varCfg.type === 'num') {
                    appletVariables[oldAppletVar.name] = {
                        idx,
                        type: 'num',
                        kind: 'deployParam',
                        name: oldAppletVar.name,
                        defaultValue: oldAppletVar.varCfg.val,
                        deployParamKind: DeployParamKind.localVarValue,
                        sparkplug: {
                            tracked: Boolean(maybeSparkplugDeviceMetrics),
                            writable: maybeSparkplugDeviceMetrics?.permissions.write ?? false,
                        },
                    };
                } else {
                    // the only other types are Error and pending, but applet variables could not have been initialized to these in the config
                }
            }
            idx++;
        }
    }

    // PROCESS FLOWS
    const flows: AppletDesignVersionConfig_V4['flows'] = Object.fromEntries(Object.entries(oldCfg.flows).map<[string, AppletDesignVersionConfig_V4['flows'][string]]>(([ flowId, flowCfg ]) => {
        return [
            flowId,
            {
                id: flowCfg.id,
                kind: 'regular',
                name: flowCfg.name,
                groups: flowCfg.groups,
                comments: flowCfg.comments,
                connections: flowCfg.connections,
                nodes: Object.fromEntries(Object.entries(flowCfg.nodes).map<[string, AppletDesignVersionConfig_V4['flows'][string]['nodes'][string]]>(([ nodeId, nodeCfg ]) => {
                    if (nodeCfg.kind === 'workInstruction') {
                        const typedOldNode = nodeCfg as NodeCfg_V3<'workInstruction'>;
                        const newNodeCfg: NodeCfg_V4<'workInstruction'> = {
                            disabled: false,
                            y: typedOldNode.y,
                            x: typedOldNode.x,
                            id: typedOldNode.id,
                            label: typedOldNode.label,
                            width: typedOldNode.width,
                            kind: 'workInstruction',
                            socketDefaults: typedOldNode.socketDefaults,
                            controlValues: {
                                location: typedOldNode.controlValues.location,
                                workInstructionVersionId: typedOldNode.controlValues.workInstructionVersionId,
                                workInstructionDetail: typedOldNode.controlValues.workInstructionDetail.kind === 'default'
                                    ? {
                                        kind: 'default',
                                        workInstructionOutputs: {},
                                    }
                                    : {
                                        kind: 'process',
                                        workInstructionOutputs: {},
                                        steps: typedOldNode.controlValues.workInstructionDetail.steps.map(s => {
                                            const ret: (NodeCfg_V4<'workInstruction'>['controlValues']['workInstructionDetail'] & { kind: 'process' })['steps'][number] = {
                                                name: s.name,
                                                processes: s.processes.map(p => {
                                                    let collectTrigger: (NodeCfg_V4<'workInstruction'>['controlValues']['workInstructionDetail'] & { kind: 'process' })['steps'][number]['processes'][number]['collectTrigger'];
                                                    if (p.collectTrigger.type === 'conditional') {
                                                        collectTrigger = migrationCondition(p.collectTrigger.conditional);
                                                    } else if (p.collectTrigger.type === 'event') {
                                                        collectTrigger = {
                                                            evalTrigger: 'event',
                                                            eventTrigger: p.collectTrigger.eventId,
                                                        };
                                                    } else if (p.collectTrigger.type === 'sparkplug') {
                                                        // NOTE: we do not expect to be encountering work instruction nodes that use sparkplug metrics as their collect trigger, but this is just incase
                                                        throw new Error('A migration for the work instruction node does not exist!');
                                                    } else if (p.collectTrigger.type === 'var') {
                                                        collectTrigger = {
                                                            boolOperation: 'and',
                                                            evalTrigger: 'varChange',
                                                            comparisons: [
                                                                {
                                                                    not: false,
                                                                    type: 'isset',
                                                                    operands: [
                                                                        {
                                                                            type: 'var',
                                                                            varId: p.collectTrigger.var,
                                                                        },
                                                                    ],
                                                                },
                                                            ],
                                                        };
                                                    } else {
                                                        const u: never = p.collectTrigger;
                                                        throw new Error(`Could not migration work instruction process collect trigger: ${u}.`);
                                                    }

                                                    const ret: (NodeCfg_V4<'workInstruction'>['controlValues']['workInstructionDetail'] & { kind: 'process' })['steps'][number]['processes'][number] = {
                                                        id: p.id,
                                                        name: p.name,
                                                        collectTrigger,
                                                        params: p.params,
                                                        dataElements: p.dataElements,
                                                    };
                                                    return ret;
                                                }),
                                            };
                                            return ret;
                                        }),
                                    },
                            },
                        };
                        return [
                            nodeId,
                            newNodeCfg as BaseNodeCfg,
                        ];
                    } else if (nodeCfg.kind === 'varAssigned') {
                        const typedOldNode = nodeCfg as NodeCfg_V3<'varAssigned'>;
                        const newNodeCfg: NodeCfg_V4<'varAssigned'> = {
                            disabled: false,
                            y: typedOldNode.y,
                            x: typedOldNode.x,
                            id: typedOldNode.id,
                            kind: 'varAssigned',
                            label: typedOldNode.label,
                            width: typedOldNode.width,
                            socketDefaults: typedOldNode.socketDefaults,
                            controlValues: {
                                varId: typedOldNode.controlValues.varName,
                                changesOnly: typedOldNode.controlValues.changesOnly,
                            },
                        };
                        return [
                            nodeId,
                            newNodeCfg as BaseNodeCfg,
                        ];
                    } else if (nodeCfg.kind === 'varRead') {
                        const typedOldNode = nodeCfg as NodeCfg_V3<'varRead'>;
                        const newNodeCfg: NodeCfg_V4<'varRead'> = {
                            disabled: false,
                            kind: 'varRead',
                            y: typedOldNode.y,
                            x: typedOldNode.x,
                            id: typedOldNode.id,
                            label: typedOldNode.label,
                            width: typedOldNode.width,
                            socketDefaults: typedOldNode.socketDefaults,
                            controlValues: {
                                varId: typedOldNode.controlValues.varName,
                            },
                        };
                        return [
                            nodeId,
                            newNodeCfg as BaseNodeCfg,
                        ];
                    } else if (nodeCfg.kind === 'varWrite') {
                        const typedOldNode = nodeCfg as NodeCfg_V3<'varWrite'>;
                        const newNodeCfg: NodeCfg_V4<'varWrite'> = {
                            disabled: false,
                            kind: 'varWrite',
                            y: typedOldNode.y,
                            x: typedOldNode.x,
                            id: typedOldNode.id,
                            label: typedOldNode.label,
                            width: typedOldNode.width,
                            socketDefaults: typedOldNode.socketDefaults,
                            controlValues: {
                                varId: typedOldNode.controlValues.varName,
                            },
                        };
                        return [
                            nodeId,
                            newNodeCfg as BaseNodeCfg,
                        ];
                    } else if (nodeCfg.kind === 'performanceDataCollect') {
                        const typedOldNode = nodeCfg as NodeCfg_V3<'performanceDataCollect'>;
                        const newNodeCfg: NodeCfg_V4<'performanceDataCollect'> = {
                            disabled: false,
                            y: typedOldNode.y,
                            x: typedOldNode.x,
                            id: typedOldNode.id,
                            label: typedOldNode.label,
                            width: typedOldNode.width,
                            kind: 'performanceDataCollect',
                            socketDefaults: typedOldNode.socketDefaults,
                            controlValues: {
                                timing: FisDataTimingEnum.Start,
                                dataLabelId: typedOldNode.controlValues.dataLabelId,
                                assetCfg: {
                                    // resetting this config. Not expecting to run into this in the field
                                    id: '',
                                },
                            },
                        };
                        return [
                            nodeId,
                            newNodeCfg as BaseNodeCfg,
                        ];
                    } else if (nodeCfg.kind === 'appletEventEmit') {
                        const newNodeCfg: NodeCfg_V4<'appletEventEmit'> = {
                            x: nodeCfg.x,
                            y: nodeCfg.y,
                            id: nodeCfg.id,
                            disabled: false,
                            label: nodeCfg.label,
                            width: nodeCfg.width,
                            kind: 'appletEventEmit',
                            socketDefaults: nodeCfg.socketDefaults,
                            controlValues: nodeCfg.controlValues as NodeCfg_V3<'appletEventEmit'>['controlValues'],
                        };
                        return [
                            nodeId,
                            newNodeCfg as BaseNodeCfg,
                        ];
                    } else if (nodeCfg.kind === 'appletEventListen') {
                        const newNodeCfg: NodeCfg_V4<'appletEventListen'> = {
                            kind: 'appletEventListen',
                            x: nodeCfg.x,
                            y: nodeCfg.y,
                            id: nodeCfg.id,
                            disabled: false,
                            label: nodeCfg.label,
                            width: nodeCfg.width,
                            socketDefaults: nodeCfg.socketDefaults,
                            controlValues: nodeCfg.controlValues as NodeCfg_V3<'appletEventListen'>['controlValues'],
                        };
                        return [
                            nodeId,
                            newNodeCfg as BaseNodeCfg,
                        ];
                    } else if (nodeCfg.kind === 'attributeMaterialLink') {
                        const newNodeCfg: NodeCfg_V4<'attributeMaterialLink'> = {
                            kind: 'attributeMaterialLink',
                            x: nodeCfg.x,
                            y: nodeCfg.y,
                            id: nodeCfg.id,
                            disabled: false,
                            label: nodeCfg.label,
                            width: nodeCfg.width,
                            socketDefaults: nodeCfg.socketDefaults,
                            controlValues: nodeCfg.controlValues as NodeCfg_V3<'attributeMaterialLink'>['controlValues'],
                        };
                        return [
                            nodeId,
                            newNodeCfg as BaseNodeCfg,
                        ];
                    } else if (nodeCfg.kind === 'boolCast') {
                        const newNodeCfg: NodeCfg_V4<'boolCast'> = {
                            kind: 'boolCast',
                            x: nodeCfg.x,
                            y: nodeCfg.y,
                            id: nodeCfg.id,
                            disabled: false,
                            label: nodeCfg.label,
                            width: nodeCfg.width,
                            socketDefaults: nodeCfg.socketDefaults,
                            controlValues: nodeCfg.controlValues as NodeCfg_V3<'boolCast'>['controlValues'],
                        };
                        return [
                            nodeId,
                            newNodeCfg as BaseNodeCfg,
                        ];
                    } else if (nodeCfg.kind === 'calendar') {
                        const newNodeCfg: NodeCfg_V4<'calendar'> = {
                            kind: 'calendar',
                            x: nodeCfg.x,
                            y: nodeCfg.y,
                            id: nodeCfg.id,
                            disabled: false,
                            label: nodeCfg.label,
                            width: nodeCfg.width,
                            socketDefaults: nodeCfg.socketDefaults,
                            controlValues: nodeCfg.controlValues as NodeCfg_V3<'calendar'>['controlValues'],
                        };
                        return [
                            nodeId,
                            newNodeCfg as BaseNodeCfg,
                        ];
                    } else if (nodeCfg.kind === 'comparisonNumber') {
                        const newNodeCfg: NodeCfg_V4<'comparisonNumber'> = {
                            kind: 'comparisonNumber',
                            x: nodeCfg.x,
                            y: nodeCfg.y,
                            id: nodeCfg.id,
                            disabled: false,
                            label: nodeCfg.label,
                            width: nodeCfg.width,
                            socketDefaults: nodeCfg.socketDefaults,
                            controlValues: nodeCfg.controlValues as NodeCfg_V3<'comparisonNumber'>['controlValues'],
                        };
                        return [
                            nodeId,
                            newNodeCfg as BaseNodeCfg,
                        ];
                    } else if (nodeCfg.kind === 'comparisonString') {
                        const newNodeCfg: NodeCfg_V4<'comparisonString'> = {
                            kind: 'comparisonString',
                            x: nodeCfg.x,
                            y: nodeCfg.y,
                            id: nodeCfg.id,
                            disabled: false,
                            label: nodeCfg.label,
                            width: nodeCfg.width,
                            socketDefaults: nodeCfg.socketDefaults,
                            controlValues: nodeCfg.controlValues as NodeCfg_V3<'comparisonString'>['controlValues'],
                        };
                        return [
                            nodeId,
                            newNodeCfg as BaseNodeCfg,
                        ];
                    } else if (nodeCfg.kind === 'createMaterial') {
                        const newNodeCfg: NodeCfg_V4<'createMaterial'> = {
                            kind: 'createMaterial',
                            x: nodeCfg.x,
                            y: nodeCfg.y,
                            id: nodeCfg.id,
                            disabled: false,
                            label: nodeCfg.label,
                            width: nodeCfg.width,
                            socketDefaults: nodeCfg.socketDefaults,
                            controlValues: nodeCfg.controlValues as NodeCfg_V3<'createMaterial'>['controlValues'],
                        };
                        return [
                            nodeId,
                            newNodeCfg as BaseNodeCfg,
                        ];
                    } else if (nodeCfg.kind === 'defaultVal') {
                        const newNodeCfg: NodeCfg_V4<'defaultVal'> = {
                            kind: 'defaultVal',
                            x: nodeCfg.x,
                            y: nodeCfg.y,
                            id: nodeCfg.id,
                            disabled: false,
                            label: nodeCfg.label,
                            width: nodeCfg.width,
                            socketDefaults: nodeCfg.socketDefaults,
                            controlValues: nodeCfg.controlValues as NodeCfg_V3<'defaultVal'>['controlValues'],
                        };
                        return [
                            nodeId,
                            newNodeCfg as BaseNodeCfg,
                        ];
                    } else if (nodeCfg.kind === 'eventDelay') {
                        const newNodeCfg: NodeCfg_V4<'eventDelay'> = {
                            kind: 'eventDelay',
                            x: nodeCfg.x,
                            y: nodeCfg.y,
                            id: nodeCfg.id,
                            disabled: false,
                            label: nodeCfg.label,
                            width: nodeCfg.width,
                            socketDefaults: nodeCfg.socketDefaults,
                            controlValues: nodeCfg.controlValues as NodeCfg_V3<'eventDelay'>['controlValues'],
                        };
                        return [
                            nodeId,
                            newNodeCfg as BaseNodeCfg,
                        ];
                    } else if (nodeCfg.kind === 'eventOneShot') {
                        const newNodeCfg: NodeCfg_V4<'eventOneShot'> = {
                            kind: 'eventOneShot',
                            x: nodeCfg.x,
                            y: nodeCfg.y,
                            id: nodeCfg.id,
                            disabled: false,
                            label: nodeCfg.label,
                            width: nodeCfg.width,
                            socketDefaults: nodeCfg.socketDefaults,
                            controlValues: nodeCfg.controlValues as NodeCfg_V3<'eventOneShot'>['controlValues'],
                        };
                        return [
                            nodeId,
                            newNodeCfg as BaseNodeCfg,
                        ];
                    } else if (nodeCfg.kind === 'eventSwitch') {
                        const newNodeCfg: NodeCfg_V4<'eventSwitch'> = {
                            kind: 'eventSwitch',
                            x: nodeCfg.x,
                            y: nodeCfg.y,
                            id: nodeCfg.id,
                            disabled: false,
                            label: nodeCfg.label,
                            width: nodeCfg.width,
                            socketDefaults: nodeCfg.socketDefaults,
                            controlValues: nodeCfg.controlValues as NodeCfg_V3<'eventSwitch'>['controlValues'],
                        };
                        return [
                            nodeId,
                            newNodeCfg as BaseNodeCfg,
                        ];
                    } else if (nodeCfg.kind === 'eventThrottle') {
                        const newNodeCfg: NodeCfg_V4<'eventThrottle'> = {
                            kind: 'eventThrottle',
                            x: nodeCfg.x,
                            y: nodeCfg.y,
                            id: nodeCfg.id,
                            disabled: false,
                            label: nodeCfg.label,
                            width: nodeCfg.width,
                            socketDefaults: nodeCfg.socketDefaults,
                            controlValues: nodeCfg.controlValues as NodeCfg_V3<'eventThrottle'>['controlValues'],
                        };
                        return [
                            nodeId,
                            newNodeCfg as BaseNodeCfg,
                        ];
                    } else if (nodeCfg.kind === 'jsonAgg') {
                        const newNodeCfg: NodeCfg_V4<'jsonAgg'> = {
                            kind: 'jsonAgg',
                            x: nodeCfg.x,
                            y: nodeCfg.y,
                            id: nodeCfg.id,
                            disabled: false,
                            label: nodeCfg.label,
                            width: nodeCfg.width,
                            socketDefaults: nodeCfg.socketDefaults,
                            controlValues: nodeCfg.controlValues as NodeCfg_V3<'jsonAgg'>['controlValues'],
                        };
                        return [
                            nodeId,
                            newNodeCfg as BaseNodeCfg,
                        ];
                    } else if (nodeCfg.kind === 'jsonParse') {
                        const newNodeCfg: NodeCfg_V4<'jsonParse'> = {
                            kind: 'jsonParse',
                            x: nodeCfg.x,
                            y: nodeCfg.y,
                            id: nodeCfg.id,
                            disabled: false,
                            label: nodeCfg.label,
                            width: nodeCfg.width,
                            socketDefaults: nodeCfg.socketDefaults,
                            controlValues: nodeCfg.controlValues as NodeCfg_V3<'jsonParse'>['controlValues'],
                        };
                        return [
                            nodeId,
                            newNodeCfg as BaseNodeCfg,
                        ];
                    } else if (nodeCfg.kind === 'materialComparison') {
                        const newNodeCfg: NodeCfg_V4<'materialComparison'> = {
                            kind: 'materialComparison',
                            x: nodeCfg.x,
                            y: nodeCfg.y,
                            id: nodeCfg.id,
                            disabled: false,
                            label: nodeCfg.label,
                            width: nodeCfg.width,
                            socketDefaults: nodeCfg.socketDefaults,
                            controlValues: nodeCfg.controlValues as NodeCfg_V3<'materialComparison'>['controlValues'],
                        };
                        return [
                            nodeId,
                            newNodeCfg as BaseNodeCfg,
                        ];
                    } else if (nodeCfg.kind === 'mqttPublish') {
                        const newNodeCfg: NodeCfg_V4<'mqttPublish'> = {
                            kind: 'mqttPublish',
                            x: nodeCfg.x,
                            y: nodeCfg.y,
                            id: nodeCfg.id,
                            disabled: false,
                            label: nodeCfg.label,
                            width: nodeCfg.width,
                        
                            socketDefaults: nodeCfg.socketDefaults,
                            controlValues: nodeCfg.controlValues as NodeCfg_V3<'mqttPublish'>['controlValues'],
                        };
                        return [
                            nodeId,
                            newNodeCfg as BaseNodeCfg,
                        ];
                    } else if (nodeCfg.kind === 'mqttSubscribe') {
                        const newNodeCfg: NodeCfg_V4<'mqttSubscribe'> = {
                            kind: 'mqttSubscribe',
                            x: nodeCfg.x,
                            y: nodeCfg.y,
                            id: nodeCfg.id,
                            disabled: false,
                            label: nodeCfg.label,
                            width: nodeCfg.width,
                        
                            socketDefaults: nodeCfg.socketDefaults,
                            controlValues: nodeCfg.controlValues as NodeCfg_V3<'mqttSubscribe'>['controlValues'],
                        };
                        return [
                            nodeId,
                            newNodeCfg as BaseNodeCfg,
                        ];
                    } else if (nodeCfg.kind === 'numCast') {
                        const newNodeCfg: NodeCfg_V4<'numCast'> = {
                            kind: 'numCast',
                            x: nodeCfg.x,
                            y: nodeCfg.y,
                            id: nodeCfg.id,
                            disabled: false,
                            label: nodeCfg.label,
                            width: nodeCfg.width,
                            socketDefaults: nodeCfg.socketDefaults,
                            controlValues: nodeCfg.controlValues as NodeCfg_V3<'numCast'>['controlValues'],
                        };
                        return [
                            nodeId,
                            newNodeCfg as BaseNodeCfg,
                        ];
                    } else if (nodeCfg.kind === 'numClamp') {
                        const newNodeCfg: NodeCfg_V4<'numClamp'> = {
                            kind: 'numClamp',
                            x: nodeCfg.x,
                            y: nodeCfg.y,
                            id: nodeCfg.id,
                            disabled: false,
                            label: nodeCfg.label,
                            width: nodeCfg.width,
                            socketDefaults: nodeCfg.socketDefaults,
                            controlValues: nodeCfg.controlValues as NodeCfg_V3<'numClamp'>['controlValues'],
                        };
                        return [
                            nodeId,
                            newNodeCfg as BaseNodeCfg,
                        ];
                    } else if (nodeCfg.kind === 'number') {
                        const newNodeCfg: NodeCfg_V4<'number'> = {
                            kind: 'number',
                            x: nodeCfg.x,
                            y: nodeCfg.y,
                            id: nodeCfg.id,
                            disabled: false,
                            label: nodeCfg.label,
                            width: nodeCfg.width,
                            socketDefaults: nodeCfg.socketDefaults,
                            controlValues: nodeCfg.controlValues as NodeCfg_V3<'number'>['controlValues'],
                        };
                        return [
                            nodeId,
                            newNodeCfg as BaseNodeCfg,
                        ];
                    } else if (nodeCfg.kind === 'script') {
                        const newNodeCfg: NodeCfg_V4<'script'> = {
                            kind: 'script',
                            x: nodeCfg.x,
                            y: nodeCfg.y,
                            id: nodeCfg.id,
                            disabled: false,
                            label: nodeCfg.label,
                            width: nodeCfg.width,
                            socketDefaults: nodeCfg.socketDefaults,
                            controlValues: nodeCfg.controlValues as NodeCfg_V3<'script'>['controlValues'],
                        };
                        return [
                            nodeId,
                            newNodeCfg as BaseNodeCfg,
                        ];
                    } else if (nodeCfg.kind === 'slice') {
                        const newNodeCfg: NodeCfg_V4<'slice'> = {
                            kind: 'slice',
                            x: nodeCfg.x,
                            y: nodeCfg.y,
                            id: nodeCfg.id,
                            disabled: false,
                            label: nodeCfg.label,
                            width: nodeCfg.width,
                            socketDefaults: nodeCfg.socketDefaults,
                            controlValues: nodeCfg.controlValues as NodeCfg_V3<'slice'>['controlValues'],
                        };
                        return [
                            nodeId,
                            newNodeCfg as BaseNodeCfg,
                        ];
                    } else if (nodeCfg.kind === 'sparkplugCommander') {
                        const newNodeCfg: NodeCfg_V4<'sparkplugCommander'> = {
                            kind: 'sparkplugCommander',
                            x: nodeCfg.x,
                            y: nodeCfg.y,
                            id: nodeCfg.id,
                            disabled: false,
                            label: nodeCfg.label,
                            width: nodeCfg.width,
                        
                            socketDefaults: nodeCfg.socketDefaults,
                            controlValues: nodeCfg.controlValues as NodeCfg_V3<'sparkplugCommander'>['controlValues'],
                        };
                        return [
                            nodeId,
                            newNodeCfg as BaseNodeCfg,
                        ];
                    } else if (nodeCfg.kind === 'sparkplugConsumer') {
                        const newNodeCfg: NodeCfg_V4<'sparkplugConsumer'> = {
                            kind: 'sparkplugConsumer',
                            x: nodeCfg.x,
                            y: nodeCfg.y,
                            id: nodeCfg.id,
                            disabled: false,
                            label: nodeCfg.label,
                            width: nodeCfg.width,
                        
                            socketDefaults: nodeCfg.socketDefaults,
                            controlValues: nodeCfg.controlValues as NodeCfg_V3<'sparkplugConsumer'>['controlValues'],
                        };
                        return [
                            nodeId,
                            newNodeCfg as BaseNodeCfg,
                        ];
                    } else if (nodeCfg.kind === 'start') {
                        const newNodeCfg: NodeCfg_V4<'start'> = {
                            kind: 'start',
                            x: nodeCfg.x,
                            y: nodeCfg.y,
                            id: nodeCfg.id,
                            disabled: false,
                            label: nodeCfg.label,
                            width: nodeCfg.width,
                            socketDefaults: nodeCfg.socketDefaults,
                            controlValues: nodeCfg.controlValues as NodeCfg_V3<'start'>['controlValues'],
                        };
                        return [
                            nodeId,
                            newNodeCfg as BaseNodeCfg,
                        ];
                    } else if (nodeCfg.kind === 'strCast') {
                        const newNodeCfg: NodeCfg_V4<'strCast'> = {
                            kind: 'strCast',
                            x: nodeCfg.x,
                            y: nodeCfg.y,
                            id: nodeCfg.id,
                            disabled: false,
                            label: nodeCfg.label,
                            width: nodeCfg.width,
                            socketDefaults: nodeCfg.socketDefaults,
                            controlValues: nodeCfg.controlValues as NodeCfg_V3<'strCast'>['controlValues'],
                        };
                        return [
                            nodeId,
                            newNodeCfg as BaseNodeCfg,
                        ];
                    } else if (nodeCfg.kind === 'text') {
                        const newNodeCfg: NodeCfg_V4<'text'> = {
                            kind: 'text',
                            x: nodeCfg.x,
                            y: nodeCfg.y,
                            id: nodeCfg.id,
                            disabled: false,
                            label: nodeCfg.label,
                            width: nodeCfg.width,
                            socketDefaults: nodeCfg.socketDefaults,
                            controlValues: nodeCfg.controlValues as NodeCfg_V3<'text'>['controlValues'],
                        };
                        return [
                            nodeId,
                            newNodeCfg as BaseNodeCfg,
                        ];
                    } else if (nodeCfg.kind === 'timer') {
                        const newNodeCfg: NodeCfg_V4<'timer'> = {
                            kind: 'timer',
                            x: nodeCfg.x,
                            y: nodeCfg.y,
                            id: nodeCfg.id,
                            disabled: false,
                            label: nodeCfg.label,
                            width: nodeCfg.width,
                            socketDefaults: nodeCfg.socketDefaults,
                            controlValues: nodeCfg.controlValues as NodeCfg_V3<'timer'>['controlValues'],
                        };
                        return [
                            nodeId,
                            newNodeCfg as BaseNodeCfg,
                        ];
                    } else if (nodeCfg.kind === 'txtFormat') {
                        const newNodeCfg: NodeCfg_V4<'txtFormat'> = {
                            kind: 'txtFormat',
                            x: nodeCfg.x,
                            y: nodeCfg.y,
                            id: nodeCfg.id,
                            disabled: false,
                            label: nodeCfg.label,
                            width: nodeCfg.width,
                            socketDefaults: nodeCfg.socketDefaults,
                            controlValues: nodeCfg.controlValues as NodeCfg_V3<'txtFormat'>['controlValues'],
                        };
                        return [
                            nodeId,
                            newNodeCfg as BaseNodeCfg,
                        ];
                    } else if (nodeCfg.kind === 'udpRead') {
                        const newNodeCfg: NodeCfg_V4<'udpRead'> = {
                            kind: 'udpRead',
                            x: nodeCfg.x,
                            y: nodeCfg.y,
                            id: nodeCfg.id,
                            disabled: false,
                            label: nodeCfg.label,
                            width: nodeCfg.width,
                        
                            socketDefaults: nodeCfg.socketDefaults,
                            controlValues: nodeCfg.controlValues as NodeCfg_V3<'udpRead'>['controlValues'],
                        };
                        return [
                            nodeId,
                            newNodeCfg as BaseNodeCfg,
                        ];
                    } else if (nodeCfg.kind === 'udpSocket') {
                        const newNodeCfg: NodeCfg_V4<'udpSocket'> = {
                            kind: 'udpSocket',
                            x: nodeCfg.x,
                            y: nodeCfg.y,
                            id: nodeCfg.id,
                            disabled: false,
                            label: nodeCfg.label,
                            width: nodeCfg.width,
                        
                            socketDefaults: nodeCfg.socketDefaults,
                            controlValues: nodeCfg.controlValues as NodeCfg_V3<'udpSocket'>['controlValues'],
                        };
                        return [
                            nodeId,
                            newNodeCfg as BaseNodeCfg,
                        ];
                    } else if (nodeCfg.kind === 'udpWrite') {
                        const newNodeCfg: NodeCfg_V4<'udpWrite'> = {
                            kind: 'udpWrite',
                            x: nodeCfg.x,
                            y: nodeCfg.y,
                            id: nodeCfg.id,
                            disabled: false,
                            label: nodeCfg.label,
                            width: nodeCfg.width,
                        
                            socketDefaults: nodeCfg.socketDefaults,
                            controlValues: nodeCfg.controlValues as NodeCfg_V3<'udpWrite'>['controlValues'],
                        };
                        return [
                            nodeId,
                            newNodeCfg as BaseNodeCfg,
                        ];
                    } else if (nodeCfg.kind === 'varWriteMultiple') {
                        const newNodeCfg: NodeCfg_V4<'varWriteMultiple'> = {
                            kind: 'varWriteMultiple',
                            x: nodeCfg.x,
                            y: nodeCfg.y,
                            id: nodeCfg.id,
                            disabled: false,
                            label: nodeCfg.label,
                            width: nodeCfg.width,
                            socketDefaults: nodeCfg.socketDefaults,
                            controlValues: nodeCfg.controlValues as NodeCfg_V3<'varWriteMultiple'>['controlValues'],
                        };
                        return [
                            nodeId,
                            newNodeCfg as BaseNodeCfg,
                        ];
                    } else if (nodeCfg.kind === 'workOrder') {
                        const newNodeCfg: NodeCfg_V4<'workOrder'> = {
                            kind: 'workOrder',
                            x: nodeCfg.x,
                            y: nodeCfg.y,
                            id: nodeCfg.id,
                            disabled: false,
                            label: nodeCfg.label,
                            width: nodeCfg.width,
                            socketDefaults: nodeCfg.socketDefaults,
                            controlValues: nodeCfg.controlValues as NodeCfg_V3<'workOrder'>['controlValues'],
                        };
                        return [
                            nodeId,
                            newNodeCfg as BaseNodeCfg,
                        ];
                    } else {
                        const u: never = nodeCfg.kind;
                        throw new Error(`Unknown node kind: ${JSON.stringify(u)}`);
                    }
                })),
            },
        ];
    }));

    // PROCESS SCREENS
    const screens: AppletDesignVersionConfig_V4['screens'] = oldCfg.screens.map<AppletDesignVersionConfig_V4['screens'][number]>(oldScreen => {
        const ret: AppletDesignVersionConfig_V4['screens'][number] = {
            id: oldScreen.id,
            name: oldScreen.name,
            order: oldScreen.order,
            activeOperator: oldScreen.operatorAuth,
            gridLayoutAspectRatios: {
                mobile: migrateGridLayout(oldScreen.gridLayoutAspectRatios.mobile),
                tablet: migrateGridLayout(oldScreen.gridLayoutAspectRatios.tablet),
                desktop: migrateGridLayout(oldScreen.gridLayoutAspectRatios.desktop),
                largeDisplay: migrateGridLayout(oldScreen.gridLayoutAspectRatios.largeDisplay),
            },
            cells: oldScreen.cells.filter(c => {
                if (c.type === 'process') {
                    // NOTE: we do not expect to be encountering process cells from any existing v3 configs, but this is just incase.
                    throw new Error('A migration for the process cell does not exist!');
                }
                if (c.type === 'macroTarget' && c.attrs.macroType === 'process') {
                    // NOTE: we do not expect to be encountering process macros from any existing v3 configs, but this is just incase.
                    throw new Error('A migration for the process macro does not exist! (encountered marcroTarget cell)');
                }
                return true;
            }).map(c => {
                if (c.type === 'attrLookup') {
                    const attrs: (AppletDesignVersionConfig_V4['screens'][number]['cells'][number] & { type: 'attrLookup' })['attrs'] = {
                        // NOTE: if this function was already async I would have ran the query here to fetch the schema name, but to convert this function to async would be a bit of a tear up since the result of this function is inside of a computed property
                        attrSchemaName: '',
                        cols: c.attrs.cols,
                        conds: c.attrs.conds,
                        showName: c.attrs.showName,
                        parentCellId: c.attrs.parentCellId,
                        attrSchemaId: c.attrs.attrSchemaId,
                        materialIdVarId: c.attrs.materialIdVar,
                    };
                    const newC: AppletDesignVersionConfig_V4['screens'][number]['cells'][number] & { type: 'attrLookup' } = {
                        attrs,
                        id: c.id,
                        type: c.type,
                        name: c.name,
                        readonly: c.readonly,
                        elevated: c.elevated,
                        modifiers: c.modifiers,
                    };
                    return newC;
                } else if (c.type === 'btn') {
                    let varBehavior: (AppletDesignVersionConfig_V4['screens'][number]['cells'][number] & { type: 'btn' })['attrs']['varBehavior'] = {
                        targetVarId: '',
                        type: 'timestamp',
                    };
                    if (c.attrs.varBehavior) {
                        if (c.attrs.varBehavior.type === 'constant' || c.attrs.varBehavior.type === 'timestamp') {
                            if (c.attrs.varBehavior.type === 'constant') {
                                varBehavior = {
                                    type: 'constant',
                                    value: c.attrs.varBehavior.value,
                                    targetVarId: c.attrs.targetVar || '',
                                };
                            } else if (c.attrs.varBehavior.type === 'timestamp') {
                                varBehavior = {
                                    type: 'timestamp',
                                    targetVarId: c.attrs.targetVar || '',
                                };
                            }
                        } else {
                            varBehavior = {
                                type: c.attrs.varBehavior.type,
                            };
                        }
                    }
                    const attrs: (AppletDesignVersionConfig_V4['screens'][number]['cells'][number] & { type: 'btn' })['attrs'] = {
                        varBehavior,
                        borderRadius: '4px',
                        label: c.attrs.label,
                        color: c.attrs.color,
                        variant: c.attrs.variant,
                        enabled: c.attrs.enabled,
                        fontSize: c.attrs.fontSize,
                        fontColor: c.attrs.fontColor,
                        parentCellId: c.attrs.parentCellId,
                        appletEventId: c.attrs.appletEventId,
                        linkedScriptId: c.attrs.linkedScriptId,
                        targetScreenId: c.attrs.targetScreenId,
                    };
                    if (c.attrs.appletEventName) {
                        const appletEventId = impliedAppletEventIds.btnClick({ cellId: c.id });
                        attrs.appletEventId = appletEventId;
                        userAppletEvents[appletEventId] = {
                            appletEventId,
                            appletEventName: c.attrs.appletEventName,
                        };
                    }
                    const newC: AppletDesignVersionConfig_V4['screens'][number]['cells'][number] & { type: 'btn' } = {
                        attrs,
                        id: c.id,
                        type: c.type,
                        name: c.name,
                        readonly: c.readonly,
                        elevated: c.elevated,
                        modifiers: c.modifiers,
                    };
                    return newC;
                } else if (c.type === 'circularProgress') {
                    const attrs: (AppletDesignVersionConfig_V4['screens'][number]['cells'][number] & { type: 'circularProgress' })['attrs'] = {
                        minValue: c.attrs.minValue,
                        maxValue: c.attrs.maxValue,
                        dialColor: c.attrs.dialColor,
                        innerText: c.attrs.innerText,
                        sourceVar: c.attrs.sourceVar,
                        parentCellId: c.attrs.parentCellId,
                        flashingCondition: c.attrs.flashingCondition ? migrateVarChangeCondition(c.attrs.flashingCondition) : null,
                    };
                    const newC: AppletDesignVersionConfig_V4['screens'][number]['cells'][number] & { type: 'circularProgress' } = {
                        attrs,
                        id: c.id,
                        type: c.type,
                        name: c.name,
                        readonly: c.readonly,
                        elevated: c.elevated,
                        modifiers: c.modifiers,
                    };
                    return newC;
                } else if (c.type === 'datetime') {
                    const attrs: (AppletDesignVersionConfig_V4['screens'][number]['cells'][number] & { type: 'datetime' })['attrs'] = {
                        useItalics: false,
                        format: c.attrs.format,
                        halign: c.attrs.halign,
                        valign: c.attrs.valign,
                        autofit: c.attrs.autofit,
                        wordWrap: c.attrs.wordWrap,
                        fontSize: c.attrs.fontSize,
                        textColor: c.attrs.textColor,
                        fontWeight: c.attrs.fontWeight,
                        parentCellId: c.attrs.parentCellId,
                    };
                    const newC: AppletDesignVersionConfig_V4['screens'][number]['cells'][number] & { type: 'datetime' } = {
                        attrs,
                        id: c.id,
                        type: c.type,
                        name: c.name,
                        readonly: c.readonly,
                        elevated: c.elevated,
                        modifiers: c.modifiers,
                    };
                    return newC;
                } else if (c.type === 'dialog') {
                    const attrs: (AppletDesignVersionConfig_V4['screens'][number]['cells'][number] & { type: 'dialog' })['attrs'] = {
                        parentCellId: c.attrs.parentCellId,
                        showDialog: migrationCondition(c.attrs.showDialog),
                    };
                    const newC: AppletDesignVersionConfig_V4['screens'][number]['cells'][number] & { type: 'dialog' } = {
                        attrs,
                        id: c.id,
                        type: c.type,
                        name: c.name,
                        readonly: c.readonly,
                        elevated: c.elevated,
                        modifiers: c.modifiers,
                    };
                    return newC;
                } else if (c.type === 'input') {
                    const attrs: (AppletDesignVersionConfig_V4['screens'][number]['cells'][number] & { type: 'input' })['attrs'] = {
                        trim: c.attrs.trim,
                        label: c.attrs.label,
                        enabled: c.attrs.enabled,
                        errorMsg: c.attrs.errorMsg,
                        writeVarId: c.attrs.writeVar,
                        captureFocus: c.attrs.captureFocus,
                        parentCellId: c.attrs.parentCellId,
                        clearCondition: c.attrs.clearCondition
                            ? migrationCondition(c.attrs.clearCondition)
                            : null,
                    };
                    const newC: AppletDesignVersionConfig_V4['screens'][number]['cells'][number] & { type: 'input' } = {
                        attrs,
                        id: c.id,
                        type: c.type,
                        name: c.name,
                        readonly: c.readonly,
                        elevated: c.elevated,
                        modifiers: c.modifiers,
                    };
                    return newC;
                } else if (c.type === 'macroTarget') {
                    const attrs: (AppletDesignVersionConfig_V4['screens'][number]['cells'][number] & { type: 'macroTarget' })['attrs'] = {
                        macroId: c.attrs.macroId,
                        macroType: 'materialLookup',
                        targetName: c.attrs.targetName,
                        parentCellId: c.attrs.parentCellId,
                    };
                    const newC: AppletDesignVersionConfig_V4['screens'][number]['cells'][number] & { type: 'macroTarget' } = {
                        attrs,
                        id: c.id,
                        type: c.type,
                        name: c.name,
                        readonly: c.readonly,
                        elevated: c.elevated,
                        modifiers: c.modifiers,
                    };
                    return newC;
                } else if (c.type === 'markdown') {
                    const newC: AppletDesignVersionConfig_V4['screens'][number]['cells'][number] & { type: 'markdown' } = c;
                    return newC;
                } else if (c.type === 'materialLookup') {
                    const attrs: (AppletDesignVersionConfig_V4['screens'][number]['cells'][number] & { type: 'materialLookup' })['attrs'] = {
                        matClassId: c.attrs.matClassId,
                        outputVars: {
                            materialIdVar: {
                                varId: c.attrs.outputVars.materialId,
                                varCfg: {
                                    idx: 999, // setting high idx to ensure generated variables are at the bottom of the list
                                    type: 'str',
                                    defaultValue: '',
                                    kind: 'generated',
                                    generatedByMacroId: c.id,
                                    name: c.attrs.outputVars.materialId,
                                    sparkplug: {
                                        tracked: false,
                                        writable: false,
                                    },
                                },
                            },
                            serialNumberVar: {
                                varId: c.attrs.outputVars.sn,
                                varCfg: {
                                    idx: 999, // setting high idx to ensure generated variables are at the bottom of the list
                                    type: 'str',
                                    defaultValue: '',
                                    kind: 'generated',
                                    generatedByMacroId: c.id,
                                    name: c.attrs.outputVars.sn,
                                    sparkplug: {
                                        tracked: false,
                                        writable: false,
                                    },
                                },
                            },
                            udfs: c.attrs.outputVars.udfs.map(u => {
                                return {
                                    udfColumnId: u.udfColumnId,
                                    varCfg: {
                                        varId: u.varName,
                                        varCfg: {
                                            idx: 999, // setting high idx to ensure generated variables are at the bottom of the list
                                            type: 'str',
                                            name: u.varName,
                                            defaultValue: '',
                                            kind: 'generated',
                                            generatedByMacroId: c.id,
                                            sparkplug: {
                                                tracked: false,
                                                writable: false,
                                            },
                                        },
                                    },
                                };
                            }),
                        },
                        parentCellId: c.attrs.parentCellId,
                        udfColsToShow: c.attrs.udfColsToShow,
                        filterConditions: c.attrs.filterConditions,
                        refreshCondition: migrationCondition(c.attrs.refreshCondition),
                    };
                    const newC: AppletDesignVersionConfig_V4['screens'][number]['cells'][number] & { type: 'materialLookup' } = {
                        attrs,
                        id: c.id,
                        type: c.type,
                        name: c.name,
                        readonly: c.readonly,
                        elevated: c.elevated,
                        modifiers: c.modifiers,
                    };
                    return newC;
                } else if (c.type === 'media') {
                    const attrs: (AppletDesignVersionConfig_V4['screens'][number]['cells'][number] & { type: 'media' })['attrs'] = {
                        items: c.attrs.items.map(i => {
                            return {
                                annotations: null,
                                id: i.id,
                                media: i.media.type === 'varForExternal'
                                    ? {
                                        varId: i.media.var,
                                        type: 'varForExternal',
                                    }
                                    : i.media,
                                varId: i.variable,
                                caption: i.caption,
                                timeout: i.timeout,
                            };
                        }),
                        parentCellId: c.attrs.parentCellId,
                    };
                    const newC: AppletDesignVersionConfig_V4['screens'][number]['cells'][number] & { type: 'media' } = {
                        attrs,
                        id: c.id,
                        type: c.type,
                        name: c.name,
                        readonly: c.readonly,
                        elevated: c.elevated,
                        modifiers: c.modifiers,
                    };
                    return newC;
                } else if (c.type === 'process') {
                    // NOTE: we do not expect to be encountering process cells from any existing v3 configs, but this is just incase.
                    throw new Error('A migration for the process cell does not exist!');
                } else if (c.type === 'processList') {
                    // NOTE: we do not expect to be encountering process list cells from any existing v3 configs, but this is just incase.
                    throw new Error('A migration for the process list cell does not exist!');
                } else if (c.type === 'text') {
                    const attrs: (AppletDesignVersionConfig_V4['screens'][number]['cells'][number] & { type: 'text' })['attrs'] = {
                        useItalics: false,
                        halign: c.attrs.halign,
                        valign: c.attrs.valign,
                        content: c.attrs.content,
                        autofit: c.attrs.autofit,
                        wordWrap: c.attrs.wordWrap,
                        fontSize: c.attrs.fontSize,
                        textColor: c.attrs.textColor,
                        fontWeight: c.attrs.fontWeight,
                        parentCellId: c.attrs.parentCellId,
                    };
                    const newC: AppletDesignVersionConfig_V4['screens'][number]['cells'][number] & { type: 'text' } = {
                        attrs,
                        id: c.id,
                        type: c.type,
                        name: c.name,
                        readonly: c.readonly,
                        elevated: c.elevated,
                        modifiers: c.modifiers,
                    };
                    return newC;
                } else if (c.type === 'timer') {
                    const attrs: (AppletDesignVersionConfig_V4['screens'][number]['cells'][number] & { type: 'timer' })['attrs'] = {
                        format: c.attrs.format,
                        maxValue: c.attrs.maxValue,
                        dialColor: c.attrs.dialColor,
                        sourceVarId: c.attrs.sourceVar,
                        parentCellId: c.attrs.parentCellId,
                        flashingThreshold: c.attrs.flashingThreshold,
                    };
                    const newC: AppletDesignVersionConfig_V4['screens'][number]['cells'][number] & { type: 'timer' } = {
                        attrs,
                        id: c.id,
                        type: c.type,
                        name: c.name,
                        readonly: c.readonly,
                        elevated: c.elevated,
                        modifiers: c.modifiers,
                    };
                    return newC;
                } else if (c.type === 'workInstruction') {
                    const newCAttrs: (AppletDesignVersionConfig_V4['screens'][number]['cells'][number] & { type: 'workInstruction' })['attrs'] = {
                        ...c.attrs,
                        // NOTE: if this function was already async I would have ran the query here to fetch the work instruction name, but to convert this function to async would be a bit of a tear up since the result of this function is inside of a computed property
                        workInstructionName: '',
                        displayCfg: c.attrs.displayCfg.kind === 'advanced'
                            ? {
                                kind: 'advanced',
                                excludedCellIds: [],
                            }
                            : c.attrs.displayCfg,
                    };
                    const newC: AppletDesignVersionConfig_V4['screens'][number]['cells'][number] & { type: 'workInstruction' } = {
                        ...c,
                        attrs: newCAttrs,
                    };
                    return newC;
                } else if (c.type === 'workInstructionProcess') {
                    const newC: AppletDesignVersionConfig_V4['screens'][number]['cells'][number] & { type: 'workInstructionProcess' } = c;
                    return newC;
                } else if (c.type === 'workOrderModal') {
                    const attrs: (AppletDesignVersionConfig_V4['screens'][number]['cells'][number] & { type: 'workOrderModal' })['attrs'] = {
                        writeVarId: c.attrs.writeVar,
                        parentCellId: c.attrs.parentCellId,
                        activateCondition: migrationCondition(c.attrs.showCondition),
                    };
                    const newC: AppletDesignVersionConfig_V4['screens'][number]['cells'][number] & { type: 'workOrderModal' } = {
                        attrs,
                        id: c.id,
                        type: c.type,
                        name: c.name,
                        readonly: c.readonly,
                        elevated: c.elevated,
                        modifiers: c.modifiers,
                    };
                    return newC;
                } else {
                    const u: never = c;
                    throw new Error(`Unknown cell type: ${u}`);
                }
            }),
        };

        return ret;
    });

    // PROCESS MACROS
    const macros: AppletDesignVersionConfig_V4['macros'] = oldCfg.macros.filter(oldMacro => oldMacro.type !== 'process').map<AppletDesignVersionConfig_V4['macros'][number]>(oldMacro => {
        if (oldMacro.type === 'materialLookup') {
            const ret: AppletDesignVersionConfig_V4['macros'][number] & { type: 'materialLookup' } = {
                id: oldMacro.id,
                type: 'materialLookup',
                attrs: {
                    materialLookupAttrs: {
                        matClassId: oldMacro.attrs.materialLookupAttrs.matClassId,
                        outputVars: {
                            materialIdVar: {
                                varId: oldMacro.attrs.materialLookupAttrs.outputVars.materialId,
                                varCfg: {
                                    idx: 999, // setting high idx to ensure generated variables are at the bottom of the list
                                    type: 'str',
                                    defaultValue: '',
                                    kind: 'generated',
                                    generatedByMacroId: oldMacro.id,
                                    name: oldMacro.attrs.materialLookupAttrs.outputVars.materialId,
                                    sparkplug: {
                                        tracked: false,
                                        writable: false,
                                    },
                                },
                            },
                            serialNumberVar: {
                                varId: oldMacro.attrs.materialLookupAttrs.outputVars.sn,
                                varCfg: {
                                    idx: 999, // setting high idx to ensure generated variables are at the bottom of the list
                                    type: 'str',
                                    defaultValue: '',
                                    kind: 'generated',
                                    generatedByMacroId: oldMacro.id,
                                    name: oldMacro.attrs.materialLookupAttrs.outputVars.sn,
                                    sparkplug: {
                                        tracked: false,
                                        writable: false,
                                    },
                                },
                            },
                            udfs: oldMacro.attrs.materialLookupAttrs.outputVars.udfs.map(u => {
                                return {
                                    udfColumnId: u.udfColumnId,
                                    varCfg: {
                                        varId: u.varName,
                                        varCfg: {
                                            idx: 999, // setting high idx to ensure generated variables are at the bottom of the list
                                            type: 'str',
                                            name: u.varName,
                                            defaultValue: '',
                                            kind: 'generated',
                                            generatedByMacroId: oldMacro.id,
                                            sparkplug: {
                                                tracked: false,
                                                writable: false,
                                            },
                                        },
                                    },
                                };
                            }),
                        },
                        parentCellId: oldMacro.attrs.materialLookupAttrs.parentCellId,
                        udfColsToShow: oldMacro.attrs.materialLookupAttrs.udfColsToShow,
                        filterConditions: oldMacro.attrs.materialLookupAttrs.filterConditions,
                        refreshCondition: migrationCondition(oldMacro.attrs.materialLookupAttrs.refreshCondition),
                    },
                },
            };
            return ret;
        } else {
            // const u: never = oldMacro;
            throw new Error('Unknown macro type');
        }
    });

    const ret: AppletDesignVersionConfig_V4 = {
        flows,
        macros,
        screens,
        appletVariables,
        schemaVersion: 4,
        userAppletEvents,
        scripts: oldCfg.scripts,
        lockout: oldCfg.lockout,
        fontFamily: oldCfg.fontFamily,
        activeOperator: oldCfg.operatorAuth,
    };
    return ret;
};
